import { Time, time } from '@distributedlab/tools'
import enDayjsLocal from 'dayjs/locale/en'
import { BigNumberish, formatUnits, Numeric } from 'ethers'

export const setDayjsLocale = (locale: 'en') => {
  const nextLocale = {
    en: enDayjsLocal,
  }[locale]

  Time.locale(nextLocale)
}

export function handleTimestamp(timestamp: number) {
  if (
    Math.abs(time().timestamp - timestamp) >
    Math.abs(time().timestamp - timestamp / 1000)
  ) {
    return time(timestamp / 1000)
  } else {
    return time(timestamp)
  }
}

export function formatBigNumber(
  value: BigNumberish,
  decimals?: string | Numeric,
  cfg?: {
    fixedPrecision?: number
  },
) {
  try {
    const formattedValue = formatUnits(value, decimals)

    if (cfg?.fixedPrecision) {
      const [integerPart, decimalPart] = formattedValue.split('.')
      const fixedDecimalPart = decimalPart
        ? decimalPart.slice(0, cfg.fixedPrecision)
        : ''
      return `${integerPart}${fixedDecimalPart ? `.${fixedDecimalPart}` : ''}`
    }

    return formattedValue
  } catch (error) {
    return '-'
  }
}

function formatAmountWithSuffix(amount: string | bigint): [string, string] {
  try {
    const suffixes = ['', 'K', 'M', 'B', 'T', 'Q', 'S', 'O', 'N']
    let suffixIndex = 0
    let formattedAmount = Number(amount)

    while (formattedAmount >= 1000 && suffixIndex < suffixes.length - 1) {
      formattedAmount /= 1000
      suffixIndex++
    }
    return [formattedAmount.toFixed(1), suffixes[suffixIndex]]
  } catch (error) {
    return ['-', '-']
  }
}

export function formatBalance(
  amount: BigNumberish,
  decimals: bigint | number = 18,
): string {
  try {
    const formattedAmount = formatUnits(String(amount), Number(decimals))
    const [convertedAmount, suffix] = formatAmountWithSuffix(formattedAmount)

    const fmtCfg = new Intl.NumberFormat(navigator.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: Math.min(4, Number(decimals)),
      roundingMode: 'trunc',
      roundingPriority: 'lessPrecision',
    })

    if (suffix) {
      return fmtCfg.format(Number(convertedAmount)) + suffix
    }

    return fmtCfg.format(Number(formattedAmount))
  } catch (error) {
    return '-'
  }
}

export function formatAddress(addr?: string, start = 6, end = 6): string {
  if (!addr) return ''
  if (addr.length <= start + end) return addr
  return `${addr.slice(0, start)}...${addr.slice(-end)}`
}

export function bnMax(...args: bigint[]) {
  return args.reduce((m, e) => (e > m ? e : m))
}

export function bnMin(...args: bigint[]) {
  return args.reduce((m, e) => (e < m ? e : m))
}

export function formatDurationFromSeconds(
  totalSecondsInput?: string | number | null,
): string | null {
  if (totalSecondsInput === null || totalSecondsInput === undefined) return null

  const totalSeconds =
    typeof totalSecondsInput === 'string'
      ? Number(totalSecondsInput)
      : totalSecondsInput

  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return null

  const unitDefinitions = [
    { label: 'Year', seconds: 60 * 60 * 24 * 365 },
    { label: 'Month', seconds: 60 * 60 * 24 * 30 },
    { label: 'Day', seconds: 60 * 60 * 24 },
    { label: 'Hour', seconds: 60 * 60 },
    { label: 'Minute', seconds: 60 },
  ]

  const unitDefinition =
    unitDefinitions.find(u => totalSeconds >= u.seconds) ??
    unitDefinitions[unitDefinitions.length - 1]

  const unitValue = Math.max(
    1,
    Math.round(totalSeconds / unitDefinition.seconds),
  )
  const unitLabel =
    unitValue === 1 ? unitDefinition.label : `${unitDefinition.label}s`

  return `${unitValue} ${unitLabel}`
}

/**
 * Parses duration string like '7200s' or '86400s' and returns formatted duration
 */
export function formatDurationString(duration?: string | null): string | null {
  if (!duration) return null

  // Parse duration string like "7200s"
  const match = duration.match(/^(\d+)s$/)
  if (!match) return null

  const seconds = Number(match[1])
  return formatDurationFromSeconds(seconds)
}

/**
 * Formats a percent value for display in UI.
 *
 * - Adds '+' or '–' sign.
 * - Clamps to ±999%.
 * - Shows '~0%' for very small values (<0.05%).
 * - Keeps 1 decimal place.
 * - Returns '0%' for exact zero.
 */
export const formatPercentDiff = (value: number): string => {
  if (!isFinite(value)) return '—'

  const clamped = Math.max(Math.min(value, 999), -999)

  if (Math.abs(clamped) < 0.05 && clamped !== 0) return '~0%'

  if (clamped === 0) return '0%'

  const sign = clamped > 0 ? '+' : ''
  const formatted = clamped.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

  return `${sign}${formatted}%`
}

export const formatUtc = (
  date: Date,
  pattern:
    | 'HH:mm'
    | 'yyyy-MM-dd HH:mm'
    | 'MM-dd'
    | 'yyyy-MM-dd' = 'yyyy-MM-dd HH:mm',
): string => {
  const pad = (n: number) => n.toString().padStart(2, '0')
  const Y = date.getUTCFullYear()
  const M = pad(date.getUTCMonth() + 1)
  const D = pad(date.getUTCDate())
  const H = pad(date.getUTCHours())
  const m = pad(date.getUTCMinutes())

  switch (pattern) {
    case 'HH:mm':
      return `${H}:${m}`
    case 'yyyy-MM-dd HH:mm':
      return `${Y}-${M}-${D} ${H}:${m}`
    case 'yyyy-MM-dd':
      return `${Y}-${M}-${D}`
    case 'MM-dd':
      return `${M}-${D}`
    default:
      return date.toISOString()
  }
}

export const formatLocalDateTime = (
  date: Date,
  pattern:
    | 'HH:mm'
    | 'yyyy-MM-dd HH:mm'
    | 'MM-dd'
    | 'yyyy-MM-dd' = 'yyyy-MM-dd HH:mm',
): string => {
  const pad = (n: number) => n.toString().padStart(2, '0')
  const Y = date.getFullYear()
  const M = pad(date.getMonth() + 1)
  const D = pad(date.getDate())
  const H = pad(date.getHours())
  const m = pad(date.getMinutes())

  switch (pattern) {
    case 'HH:mm':
      return `${H}:${m}`
    case 'yyyy-MM-dd HH:mm':
      return `${Y}-${M}-${D} ${H}:${m}`
    case 'yyyy-MM-dd':
      return `${Y}-${M}-${D}`
    case 'MM-dd':
      return `${M}-${D}`
    default:
      return date.toLocaleString()
  }
}

export function formatCurrency(
  amount: number,
  {
    locale = 'en-US',
    currency = 'USD',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  }: {
    locale?: string
    currency?: string
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  } = {},
) {
  return amount.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  })
}

export function formatBridgeAtomic(amount: string, exponent = 18) {
  return formatUnits(amount ?? '0', exponent)
}

type FormatAtomicCompactOptions = {
  decimals?: number
  exponent?: number
  minDisplay?: string
}

export function formatAtomicCompact(
  atomic: string,
  {
    exponent = 18,
    decimals = 4,
    minDisplay = `< 0.${'0'.repeat(decimals - 1)}1`,
  }: FormatAtomicCompactOptions = {},
): string {
  if (!atomic || atomic === '0') return '0'

  let value: bigint
  try {
    value = BigInt(atomic)
  } catch {
    return '0'
  }

  const negative = value < 0n
  const abs = negative ? -value : value

  const minVisibleAtomic = 10n ** BigInt(exponent - decimals)

  if (abs > 0n && abs < minVisibleAtomic) {
    return minDisplay
  }

  const raw = abs.toString().padStart(exponent + 1, '0')
  const integer = raw.slice(0, -exponent)

  const fullFraction = raw.slice(-exponent)
  const trimmedFraction = fullFraction.slice(0, decimals).replace(/0+$/, '')

  return `${negative ? '-' : ''}${integer}${
    trimmedFraction ? `.${trimmedFraction}` : ''
  }`
}

export function parseSharesAmount(sharesValue?: string): string {
  if (!sharesValue) return '0'
  const parts = sharesValue.split('.')
  return parts[0]
}
